import { DeleteOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Col, Form, InputNumber, Popconfirm, Row, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import axios from "axios";
import { message } from "components/Messages/Messages";
import RelevantStudyYearsDisplay from "components/RelevantStudyYearsDisplay/RelevantStudyYearsDisplay";
import { SharedQuota, StudyProgramQuota } from "interfaces/events";
import React, { useEffect, useState } from "react";

interface Props {
  currentSharedQuota: SharedQuota | undefined;
  sharedQuotas: SharedQuota[];
  studyProgramQuotas: StudyProgramQuota[];
  handleQuotas: (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => void;
}

const { Text } = Typography;

const TotalSeatsStep: React.FC<Props> = ({ sharedQuotas, studyProgramQuotas, currentSharedQuota, handleQuotas }) => {
  const [connectedQuotas, setConnectedQuotas] = useState<(SharedQuota & { studyProgramQuotas: StudyProgramQuota[] })[]>(
    []
  );
  const [form] = useForm();
  const [loadingSave, setLoadingSave] = useState<boolean>(false);

  useEffect(() => {
    if (currentSharedQuota) {
      form.setFieldsValue({
        max_students_allowed: currentSharedQuota.max_students_allowed,
      });
    }
  }, [currentSharedQuota, form]);

  useEffect(() => {
    if (sharedQuotas.length < 1) return;
    const connected = sharedQuotas.map(sharedquota => ({
      ...sharedquota,
      studyProgramQuotas: studyProgramQuotas.filter(studyquota => studyquota.shared_quota === sharedquota.id),
    }));
    setConnectedQuotas(connected);
  }, [sharedQuotas, studyProgramQuotas]);

  const saveSharedQuota = () => {
    if (form.getFieldValue("max_students_allowed") === currentSharedQuota?.max_students_allowed) {
      form.validateFields().then(() => {
        handleQuotas(sharedQuotas, studyProgramQuotas, undefined);
      });
      return;
    }
    form.validateFields().then(values => {
      setLoadingSave(true);
      axios
        .put(`/events/shared-quotas/${currentSharedQuota?.id}/`, {
          ...currentSharedQuota,
          max_students_allowed: values.max_students_allowed,
        })
        .then(response => {
          const newSharedQuotas = sharedQuotas.map(quota => (quota.id === response.data.id ? response.data : quota));
          handleQuotas(newSharedQuotas, studyProgramQuotas, undefined);
          message("success", "Kvoten er lagret.");
        })
        .catch(() => message("error", "Noe gikk galt ved lagring av kvote."))
        .finally(() => setLoadingSave(false));
    });
  };

  const deleteSharedQuota = (id: number) => {
    axios
      .delete(`/events/shared-quotas/${id}/`)
      .then(() => {
        const newSharedQuotas = sharedQuotas.filter(quota => quota.id !== id);
        const newStudyProgramQuotas = studyProgramQuotas.filter(quota => quota.shared_quota !== id);
        handleQuotas(newSharedQuotas, newStudyProgramQuotas, currentSharedQuota);
        setConnectedQuotas(connectedQuotas.filter(quota => quota.id !== id));
        message("success", "Kvoten er slettet.");
      })
      .catch(() => message("error", "Noe gikk galt ved sletting av kvote."));
  };

  const deleteStudyProgramQuota = (id: number) => {
    const shared_quota = connectedQuotas.find(quota => quota.studyProgramQuotas.some(spq => spq.id === id));
    if (shared_quota?.studyProgramQuotas.length === 1) {
      // If this is the last study program quota in the shared quota, delete the shared quota
      deleteSharedQuota(shared_quota.id);
      return;
    }
    if (
      (shared_quota?.max_students_allowed ?? 0) >
      (shared_quota?.studyProgramQuotas.reduce((sum, spq) => (spq.id !== id ? sum + spq.students_allowed : sum), 0) ??
        0)
    ) {
      return message(
        "error",
        "Kan ikke slette linjen",
        "Slettes linjen vil kvoten ha flere totale plasser enn summen av plassene per linje. Juster ned totale plasser først."
      );
    }
    const newStudyProgramQuotas = studyProgramQuotas.filter(quota => quota.id !== id);
    axios
      .delete(`/events/${currentSharedQuota?.event}/study-program-quotas/${id}/`)
      .then(() => {
        handleQuotas(sharedQuotas, newStudyProgramQuotas, currentSharedQuota);
        message("success", "Linjen er slettet fra kvoten");
      })
      .catch(() => message("error", "Noe gikk galt ved sletting av linje"));
  };

  return (
    <>
      <Card title="2. Plasser per kvote" style={{ margin: "20px 0" }}>
        <Row gutter={[16, 16]}>
          {connectedQuotas.length < 1 && (
            <Card style={{ width: "100%" }}>
              Ingen kvoter er opprettet ennå. Opprett første kvote ved å legge til trinn/linje, og huke av for &quot;Ny
              kvote&quot;.
            </Card>
          )}
          {connectedQuotas.map((quota, index) => {
            return (
              <Col span={24} key={quota.id}>
                <Badge.Ribbon text={`Kvote ${index + 1}`} placement="end">
                  <Card
                    style={
                      quota.id === currentSharedQuota?.id
                        ? { background: "var(--neutral-alt)", boxShadow: "1px 1px 2px black" }
                        : undefined
                    }
                  >
                    <Row gutter={[8, 8]}>
                      <Col md={6} xs={12}>
                        <Text strong>Linjer:</Text>
                        {quota.studyProgramQuotas.map(studyProgramQuota => (
                          <Row key={studyProgramQuota.id} style={{ alignItems: "center", height: "auto" }}>
                            {currentSharedQuota?.id === quota.id && (
                              <DeleteOutlined onClick={() => deleteStudyProgramQuota(studyProgramQuota.id)} />
                            )}
                            <RelevantStudyYearsDisplay yearLevelsAccepted={studyProgramQuota.year_levels_accepted} />
                            {studyProgramQuota.study_programme_shortname}
                          </Row>
                        ))}
                      </Col>

                      <Col md={5} xs={12}>
                        <Text strong>Maks per linje:</Text>
                        {quota.studyProgramQuotas.map(studyProgramQuota => (
                          <Row key={studyProgramQuota.id} style={{ height: "25px", alignItems: "center" }}>
                            <Text>{studyProgramQuota.students_allowed}</Text>
                          </Row>
                        ))}
                      </Col>

                      <Col md={5} xs={24}>
                        <Text strong>Plasser totalt:</Text> <br />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "calc(100% - 24px)",
                          }}
                        >
                          {currentSharedQuota?.id === quota.id ? (
                            <Form form={form}>
                              <Form.Item
                                name="max_students_allowed"
                                rules={[
                                  { required: true, message: "Angi hvor mange plasser linjene i kvoten får totalt" },
                                  {
                                    validator: (_, value) => {
                                      const maxAllowed = quota.studyProgramQuotas.reduce(
                                        (sum, spq) => sum + spq.students_allowed,
                                        0
                                      );
                                      if (value > maxAllowed) {
                                        return Promise.reject(
                                          new Error(
                                            `Antall plasser kan ikke overstige summen av tillatte plasser per linje (${maxAllowed})`
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <InputNumber />
                              </Form.Item>
                            </Form>
                          ) : (
                            quota.max_students_allowed
                          )}
                        </div>
                      </Col>

                      <Col md={4} xs={24}>
                        <Text strong>Påmeldt:</Text> <br />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "calc(100% - 24px)",
                          }}
                        >
                          0 {/* TODO FIX THIS */}
                        </div>
                      </Col>

                      <Col md={3} xs={24}>
                        <div
                          style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}
                        >
                          <Button
                            onClick={() =>
                              currentSharedQuota?.id === quota.id
                                ? saveSharedQuota()
                                : handleQuotas(
                                    sharedQuotas,
                                    studyProgramQuotas,
                                    currentSharedQuota?.id === quota.id ? undefined : quota
                                  )
                            }
                            size="small"
                            style={{ width: "100%", marginBottom: "2px" }}
                            loading={loadingSave && currentSharedQuota?.id === quota.id}
                          >
                            {currentSharedQuota?.id === quota.id ? "Lagre" : "Velg"}
                          </Button>
                          <Popconfirm
                            title="Slett kvote"
                            okText="Slett"
                            cancelText="Avbryt"
                            onConfirm={() => deleteSharedQuota(quota.id)}
                            disabled={quota.id === currentSharedQuota?.id}
                          >
                            <Button
                              danger
                              style={{ width: "100%" }}
                              size="small"
                              disabled={quota.id === currentSharedQuota?.id}
                            >
                              Slett
                            </Button>
                          </Popconfirm>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          })}
        </Row>
      </Card>
    </>
  );
};

export default TotalSeatsStep;
